// Portal-page pure helpers (number/date formatting, result unwrap, today).
import type { ApiResult } from "../../api";
import { intlLocale } from "../../i18n/config";

export function fmtNum(n: number, decimals = 1): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function fmtSigned(n: number, decimals = 1): string {
  const r = Number(n.toFixed(decimals));
  if (r === 0) return fmtNum(0, decimals);
  return `${r > 0 ? "+" : ""}${fmtNum(r, decimals)}`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(intlLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(intlLocale(), {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * A compact localized relative time for the activity feed: "now", "5m", "2h",
 * "3d" via Intl.RelativeTimeFormat (so 繁中 reads naturally), falling back to a
 * short date for anything older than ~6 days. Empty string for unparseable input.
 */
export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const diffMs = then.getTime() - Date.now();
  const sec = Math.round(diffMs / 1000);
  const absSec = Math.abs(sec);
  const rtf = new Intl.RelativeTimeFormat(intlLocale(), {
    numeric: "auto",
    style: "short",
  });
  if (absSec < 60) return rtf.format(Math.round(sec), "second");
  const min = Math.round(sec / 60);
  if (Math.abs(min) < 60) return rtf.format(min, "minute");
  const hr = Math.round(min / 60);
  if (Math.abs(hr) < 24) return rtf.format(hr, "hour");
  const day = Math.round(hr / 24);
  if (Math.abs(day) <= 6) return rtf.format(day, "day");
  return fmtDate(iso);
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/** Numeric value from an engine Result, or null when it didn't compute. */
export function resultValue(r: ApiResult | undefined | null): number | null {
  if (!r || !r.ok || r.value === null) return null;
  return r.value;
}
